import React, { PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { SessionUpdateEvent } from "openai/resources/beta/realtime/realtime";
import { PublicConfigurationContext } from "../Context";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { generateEmptyAudioStream } from "./RTCConnection";
import { getAICoachConfig } from "@aidkitorg/types/lib/config";
import { OpenAIWebRTCProvider, WebRTCContext } from "../Contexts/WebRTCContext";
import { ChatSessionContext, DefaultChatSessionProvider } from "../Contexts/ChatSessionContext";
import { ChatConversationProvider, ChatProvider, useChat } from "../Contexts/ChatContext";

export type AICoachState = {
  enabled: boolean,
  active: boolean,
  ready: boolean,
  config?: ReturnType<typeof getAICoachConfig>,
  startSession: () => void,
  stopSession: () => void,
  modality: "text" | "audio",
  setModality: (v: AICoachState["modality"]) => void,
  audioElement: HTMLAudioElement | null,
  setAudioElement: (v: AICoachState["audioElement"]) => void,
  audioInputStream: MediaStream | null,
  phase: 'unknown' | 'listening' | 'speaking' | 'thinking'
  startAudioInput: (stream: MediaStream) => void,
  stopAudioInput: () => void
};

const defaults: AICoachState = {
  enabled: false,
  phase: 'unknown',
  active: false,
  ready: false,
  startSession: () => { console.warn("startSession has not been implemented, AICoachContext not found"); },
  stopSession: () => { console.warn("stopSession has not been implemented, AICoachContext not found"); },
  modality: "text",
  setModality: () => { console.warn("setModality has not been implemented, AICoachContext not found"); },
  audioElement: null,
  setAudioElement: () => { console.warn("setAudioElement has not been implemented, AICoachContext not found"); },
  audioInputStream: null,
  startAudioInput: () => { console.warn("startAudioInput has not been implemented, AICoachContext not found"); },
  stopAudioInput: () => { console.warn("stopAudioInput has not been implemented, AICoachContext not found"); }
}

export const AICoachContext = React.createContext<AICoachState>(defaults);
export function AICoachProvider({ children, ...props }: PropsWithChildren) {
  const [modality, setModality] = useState<AICoachState["modality"]>("text");
  return <ChatConversationProvider>
    <OpenAIWebRTCProvider modality={modality}>
      <ChatProvider modality={modality}>
        <DefaultChatSessionProvider>
          <AICoachProviderInner {...props} {...{ modality, setModality }}>
            {children}
          </AICoachProviderInner>
        </DefaultChatSessionProvider>
      </ChatProvider>
    </OpenAIWebRTCProvider>
  </ChatConversationProvider>;
}
export function AICoachProviderInner({ children, modality, setModality }: PropsWithChildren & Pick<AICoachState, "modality" | "setModality">) {
  const config = useContext(PublicConfigurationContext);
  const [isStarted, setIsStarted] = useState<boolean>(false);
  const [audioElement, setAudioElement] = useState<AICoachState["audioElement"]>(null);
  const [audioInputStream, setAudioInputStream] = useState<MediaStream | null>(null);
  const webrtc = useContext(WebRTCContext);
  const chatSession = useContext(ChatSessionContext);
  const chat = useChat();

  useEffect(() => {
    const featureEnabled = !!config?.capabilities?.aiCoach && !config?.capabilities?.aiCoach?.disabled;
    const isEnabled = isStarted && featureEnabled;
    chat.setEnabled(isEnabled);
    chatSession.setEnabled(isEnabled);
  }, [isStarted, config]);

  // if we have both an audio element and an output stream
  // bind the stream to the audio element
  useEffect(() => {
    if (audioElement && webrtc.outputStream) {
      audioElement.srcObject = webrtc.outputStream;
    }
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.srcObject = null;
      }
    }
  }, [audioElement, webrtc.outputStream]);

  // manage webRTC sender media stream
  // there has to be an audio media stream present on the webRTC sender
  // so if we change our input modality, call replaceTrack on the sender
  useAsyncEffect(async () => {
    // when audio stream is present, add it as the sender track
    if (audioInputStream && webrtc.sender) {
      await webrtc.sender.replaceTrack(audioInputStream.getTracks()[0]);
    }
    // when no audio stream is present, add an empty audio track
    else if (!audioInputStream && webrtc.sender) {
      await webrtc.sender.replaceTrack(generateEmptyAudioStream().getTracks()[0]);
    }
  }, [audioInputStream, webrtc.sender]);

  // update session modality
  useEffect(() => {
    if (modality === "text") {
      stopAudioInput();
    }
    if (webrtc.active) {
      webrtc.updateSession({
        modalities: modality === "audio"
          ? ["text", "audio"]
          : ["text"]
      } as SessionUpdateEvent.Session);
    }
  }, [modality, webrtc.active]);

  function startAudioInput(stream: MediaStream) {
    setAudioInputStream(stream);
  }

  function stopAudioInput() {
    if (audioInputStream) {
      audioInputStream.getTracks().forEach(track => track.stop());
      setAudioInputStream(null);
    }
  }

  const stateValue: AICoachState = {
    enabled: !!config.capabilities?.aiCoach && !config.capabilities?.aiCoach?.disabled,
    ready: chat.ready,
    phase: chat.phase,
    active: chat.active,
    startSession: () => setIsStarted(true),
    stopSession: () => setIsStarted(false),
    config: config.capabilities?.aiCoach,
    modality,
    setModality,
    audioElement, setAudioElement,
    audioInputStream,
    startAudioInput,
    stopAudioInput,
  };
  
  return <AICoachContext.Provider value={stateValue}>
    {children}
  </AICoachContext.Provider>
}

// sugar
export function useAICoach(): AICoachState {
  const AICoach = useContext(AICoachContext);
  return AICoach;
}
